import { useCallback, useEffect, useState } from "react";
import {
  MessagesSquare,
  User,
  Bot,
  AlertTriangle,
  ThumbsUp,
  ThumbsDown,
  AlertCircle,
  Clock,
  Cpu,
  FileText,
  MessageSquareWarning,
} from "lucide-react";
import { Card, CardHeader } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import {
  getConversation,
  type ConversationDetail as ConversationDetailPayload,
  type ConversationMessage,
} from "@/lib/api";
import { absolute, relative } from "@/lib/format";
import { cn } from "@/lib/cn";

/**
 * Right pane of the Conversations page. Loads the full thread for the
 * currently selected conversation, renders every message with citations,
 * grounding flag, and assistant telemetry.
 *
 * The detail re-fetches whenever `conversationId` changes — keep that
 * prop stable in the parent (URL-derived).
 */
export function ConversationDetail({ conversationId }: { conversationId: string | null }) {
  const [data, setData]       = useState<ConversationDetailPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const load = useCallback(async (id: string) => {
    setLoading(true); setError(null);
    try {
      const d = await getConversation(id);
      setData(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!conversationId) { setData(null); setError(null); return; }
    void load(conversationId);
  }, [conversationId, load]);

  if (!conversationId) {
    return (
      <Card className="flex max-h-[calc(100vh-260px)] items-center justify-center px-6 py-12">
        <EmptyState
          icon={MessagesSquare}
          title="Select a conversation"
          description="Pick one from the list to see the full thread, citations, and telemetry."
        />
      </Card>
    );
  }

  if (loading && !data) {
    return (
      <Card className="flex max-h-[calc(100vh-260px)] flex-col">
        <DetailSkeleton />
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="px-5 py-4">
        <div className="flex items-start gap-3">
          <AlertCircle size={16} strokeWidth={1.75} className="mt-0.5 shrink-0 text-danger" />
          <div className="flex-1">
            <p className="text-sm font-medium text-text-strong">Couldn't load conversation</p>
            <p className="mt-1 text-xs text-text-soft">{error}</p>
          </div>
        </div>
      </Card>
    );
  }

  if (!data) return null;

  const { conversation, messages } = data;
  const assistantMessages = messages.filter((m) => m.role === "assistant");
  const noRetrievalCount = assistantMessages.filter((m) => !m.grounded).length;
  const negativeCount = assistantMessages.filter((m) => m.feedback === "negative").length;
  const positiveCount = assistantMessages.filter((m) => m.feedback === "positive").length;

  return (
    <Card className="flex max-h-[calc(100vh-260px)] flex-col overflow-hidden">
      <CardHeader
        title="Conversation"
        subtitle={`Started ${relative(conversation.started_at)} · ${conversation.message_count} messages`}
      />

      {/* Stats strip */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-line px-5 pb-4 pt-2 text-xs text-text-soft">
        {positiveCount > 0 && (
          <span className="inline-flex items-center gap-1">
            <ThumbsUp size={12} strokeWidth={1.75} className="text-success" />
            <span className="tabular-nums">{positiveCount}</span> positive
          </span>
        )}
        {negativeCount > 0 && (
          <span className="inline-flex items-center gap-1">
            <ThumbsDown size={12} strokeWidth={1.75} className="text-danger" />
            <span className="tabular-nums">{negativeCount}</span> negative
          </span>
        )}
        {noRetrievalCount > 0 && (
          <span className="inline-flex items-center gap-1">
            <AlertTriangle size={12} strokeWidth={1.75} className="text-warning" />
            <span className="tabular-nums">{noRetrievalCount}</span> answered without grounding
          </span>
        )}
        <span
          className="ml-auto cursor-default tabular-nums text-text-faint"
          title={`Session: ${conversation.session_id}`}
        >
          {absolute(conversation.started_at)}
        </span>
      </div>

      {/* Thread */}
      <div className="flex-1 overflow-y-auto scrollbar-thin px-5 py-4">
        {messages.length === 0 ? (
          <p className="text-sm text-text-soft">This conversation has no messages.</p>
        ) : (
          <ol className="space-y-4">
            {messages.map((m) => (
              <MessageRow key={m.id} message={m} />
            ))}
          </ol>
        )}
      </div>
    </Card>
  );
}

function MessageRow({ message }: { message: ConversationMessage }) {
  const isUser = message.role === "user";
  const isSystem = message.role === "system";

  if (isSystem) {
    // System prompts are persisted for auditing but rarely useful in the UI.
    // Render them collapsed so they don't dominate the thread.
    return (
      <li>
        <details className="rounded border border-line bg-surface-2 px-3 py-2 text-xs text-text-soft">
          <summary className="cursor-pointer select-none font-medium">
            System message
            <span className="ml-2 text-text-faint">· {relative(message.created_at)}</span>
          </summary>
          <pre className="mt-2 whitespace-pre-wrap break-words font-mono text-2xs">
            {message.content}
          </pre>
        </details>
      </li>
    );
  }

  return (
    <li className={cn("flex gap-3", isUser ? "flex-row" : "flex-row")}>
      <div
        className={cn(
          "flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
          isUser ? "bg-surface-3 text-text-soft" : "bg-ink text-white",
        )}
        aria-hidden
      >
        {isUser
          ? <User size={14} strokeWidth={1.75} />
          : <Bot size={14} strokeWidth={1.75} />}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-2xs text-text-faint">
          <span className="font-medium text-text-soft">
            {isUser ? "Visitor" : "Bot"}
          </span>
          <span title={absolute(message.created_at)} className="cursor-default">
            {relative(message.created_at)}
          </span>
          {!isUser && !message.grounded && (
            <span className="inline-flex items-center gap-1 rounded bg-warning-soft px-1.5 py-0.5 text-warning">
              <AlertTriangle size={10} strokeWidth={1.75} />
              No retrieval
            </span>
          )}
          {message.feedback === "positive" && (
            <span className="inline-flex items-center gap-1 rounded bg-success-soft px-1.5 py-0.5 text-success">
              <ThumbsUp size={10} strokeWidth={1.75} />
              Positive
            </span>
          )}
          {message.feedback === "negative" && (
            <span className="inline-flex items-center gap-1 rounded bg-danger-soft px-1.5 py-0.5 text-danger">
              <ThumbsDown size={10} strokeWidth={1.75} />
              Negative
            </span>
          )}
        </div>

        <div
          className={cn(
            "mt-1 whitespace-pre-wrap break-words rounded border px-3 py-2 text-sm",
            isUser
              ? "border-line bg-surface-2 text-text-strong"
              : "border-line bg-surface text-text-strong",
          )}
        >
          {message.content}
        </div>

        {message.feedback_comment && (
          <p className="mt-1 text-2xs italic text-text-soft">
            Visitor note: {message.feedback_comment}
          </p>
        )}

        {!isUser && message.citations.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="text-2xs text-text-faint">Citations:</span>
            {message.citations.map((c) => (
              <span
                key={c.document_id}
                className="inline-flex items-center gap-1 rounded border border-line bg-surface-2 px-1.5 py-0.5 text-2xs text-text-soft"
                title={c.source_type}
              >
                <FileText size={10} strokeWidth={1.75} />
                <span className="truncate">{c.title}</span>
              </span>
            ))}
          </div>
        )}

        {!isUser && !message.grounded && message.citations.length === 0 && (
          <p className="mt-2 inline-flex items-center gap-1 text-2xs text-text-soft">
            <MessageSquareWarning size={10} strokeWidth={1.75} className="text-warning" />
            Bot answered without any retrieved context.
          </p>
        )}

        {!isUser && (message.model_used || message.latency_ms !== null || message.input_tokens !== null) && (
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-2xs text-text-faint">
            {message.model_used && (
              <span className="inline-flex items-center gap-1">
                <Cpu size={10} strokeWidth={1.75} />
                {message.model_used}
              </span>
            )}
            {message.latency_ms !== null && (
              <span className="inline-flex items-center gap-1">
                <Clock size={10} strokeWidth={1.75} />
                <span className="tabular-nums">{message.latency_ms}</span>ms
              </span>
            )}
            {message.input_tokens !== null && message.output_tokens !== null && (
              <span className="tabular-nums">
                {message.input_tokens} in / {message.output_tokens} out
              </span>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

function DetailSkeleton() {
  return (
    <>
      <div className="px-5 pt-5">
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="mt-1.5 h-3 w-1/2" />
      </div>
      <div className="border-b border-line px-5 pb-4 pt-2">
        <Skeleton className="h-3 w-1/3" />
      </div>
      <div className="flex-1 space-y-4 px-5 py-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="h-7 w-7 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3 w-1/4" />
              <Skeleton className="h-12 w-full" />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
